import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
export const frontmatter = {
  "title": "Learning",
  "lead": "In at least 15 countries in sub-Saharan Africa, over half the students do not achieve the minimum proficiency level in reading at the end of primary education."
};
function _createMdxContent(props) {
  const _components = {
    a: "a",
    blockquote: "blockquote",
    h2: "h2",
    li: "li",
    p: "p",
    strong: "strong",
    ul: "ul",
    ..._provideComponents(),
    ...props.components
  }, {Figure, HighlightByIndex, IndicatorReference, RelatedMaterial, ScrollyArea, ScrollySection, ScrollySticky} = _components;
  if (!Figure) _missingMdxReference("Figure", true);
  if (!HighlightByIndex) _missingMdxReference("HighlightByIndex", true);
  if (!IndicatorReference) _missingMdxReference("IndicatorReference", true);
  if (!RelatedMaterial) _missingMdxReference("RelatedMaterial", true);
  if (!ScrollyArea) _missingMdxReference("ScrollyArea", true);
  if (!ScrollySection) _missingMdxReference("ScrollySection", true);
  if (!ScrollySticky) _missingMdxReference("ScrollySticky", true);
  return _jsxs(_Fragment, {
    children: [_jsx(Figure, {
      id: "HERO_LEARNING"
    }), "\n", _jsxs(_components.blockquote, {
      children: ["\n", _jsx(_components.p, {
        children: "Getting children into school is not enough. Without taking away from the many other worthwhile purposes that schools do and must pursue, students need to be able to read and to carry out basic mathematics. These are the foundation for other learning, for individual empowerment and wellbeing, as well as for social, political and economic outcomes."
      }), "\n"]
    }), "\n", _jsx(_components.h2, {
      id: "children-and-youth",
      children: "Children and youth"
    }), "\n", _jsxs(IndicatorReference, {
      id: "4.1.1",
      label: "4.1.1a",
      children: [_jsxs(_components.p, {
        children: ["Many children in some of the world’s poorest countries lack basic reading and mathematics skills.\nEarly grade reading assessments in northern states of Nigeria, such as Kaduna and Sokoto, and in provinces of the Democratic Republic of Congo, such as Equateur and Katanga, for instance, suggest that over 80% of grade 3 students in primary school could not even read a ", _jsx(_components.strong, {
          children: "single word"
        }), " of connected text."]
      }), _jsx(Figure, {
        id: "EGRA"
      })]
    }), "\n", _jsx(_components.p, {
      children: "This data does not cover the children who are not in school. Surveys that are carried out at people's homes can capture the skills of both children who attend school and those who do not."
    }), "\n", _jsx(_components.p, {
      children: "For instance, the graphs below look at different regions in Pakistan and rural India, based on citizen-led assessments."
    }), "\n", _jsxs(_components.p, {
      children: ["The ", _jsx(HighlightByIndex, {
        index: "1",
        children: "red lines"
      }), " show the percentage of children ", _jsx(_components.strong, {
        children: "in school"
      }), " that have basic reading skills by grade, while the ", _jsx(HighlightByIndex, {
        index: "0",
        children: "blue lines"
      }), " show the percentage of all children who have basic reading skills, ", _jsx(_components.strong, {
        children: "whether in school or not by the age that corresponds to their grade"
      }), "."]
    }), "\n", _jsx(_components.p, {
      children: "The gaps between the two lines are a reminder that in countries with high out-of-school rates, policy makers get only a partial picture if they only monitor learning of those in school. It is important to note that the monitored level of skill is at a very low level. Even in the best performing regions, at least some adolescents in secondary school cannot read a grade 2 text."
    }), "\n", _jsx(Figure, {
      id: "LEARN_AGE_GRADE"
    }), "\n", _jsx(IndicatorReference, {
      id: "4.1.1",
      label: "4.1.1b",
      children: _jsx(_components.p, {
        children: "It is estimated that nearly 40% of primary students do not have basic reading skills. This is true for 70% of students in sub-Saharan Africa but only 3% of students in Europe and Northern America."
      })
    }), "\n", _jsxs(IndicatorReference, {
      id: "4.1.0",
      label: "4.1.0",
      children: [_jsx(_components.p, {
        children: "In many countries, there is no guarantee that children will be able to read even if they have completed primary school. Globally, of those who completed primary education, four out of ten did not have basic reading skills. This rises to 8 out of ten in low-income countries, as the graph below shows."
      }), _jsxs(ScrollyArea, {
        children: [_jsx(ScrollySticky, {
          children: _jsx(Figure, {
            id: "LEARN_REACHFINAL"
          })
        }), _jsx(ScrollySection, {
          children: _jsxs(_components.p, {
            children: ["The ", _jsx(HighlightByIndex, {
              index: "-1",
              children: "grey sections"
            }), " show the percentage of children who complete primary school but do not reach a minimum level of proficiency in reading. The ", _jsx(HighlightByIndex, {
              index: "1",
              children: "red sections"
            }), " show those students who complete primary and reach a minimum proficiency, and the ", _jsx(HighlightByIndex, {
              index: "0",
              children: "blue sections"
            }), " those children who do not complete primary education."]
          })
        })]
      })]
    }), "\n", _jsx(_components.p, {
      children: "Looking at the country level, in Benin and Nigeria, about 85% of young people who left school after 5 or 6 years were not literate. Some countries have much better rates, however. Almost all those who left school after grades 5 or 6 in Costa Rica and Rwanda were literate, for instance."
    }), "\n", _jsxs(ScrollyArea, {
      children: [_jsx(ScrollySticky, {
        children: _jsx(Figure, {
          id: "LITBYYRS"
        })
      }), _jsx(ScrollySection, {
        id: "KEN",
        children: _jsxs(_components.p, {
          children: ["In ", _jsx(_components.strong, {
            children: "Kenya"
          }), ", for instance, one in four are literate ", _jsx(HighlightByIndex, {
            index: "1",
            children: "with up to 4 years of schooling"
          }), ", but this jumps to three out of four for those who have ", _jsx(HighlightByIndex, {
            index: "0",
            children: "5-6 years of schooling"
          }), " – the equivalent of a primary education."]
        })
      }), _jsx(ScrollySection, {
        id: "BFA",
        children: _jsxs(_components.p, {
          children: ["In ", _jsx(_components.strong, {
            children: "Burkina Faso"
          }), ", barely any children emerge ", _jsx(HighlightByIndex, {
            index: "1",
            children: "after 4 years of schooling"
          }), " able to read, but more than half develop basic literacy skills if they make it through the ", _jsx(HighlightByIndex, {
            index: "0",
            children: "full course of primary education"
          }), "."]
        })
      })]
    }), "\n", _jsx(_components.p, {
      children: "In order to be able to compare learning across countries, assessments or tests need to be matched with international standards using one common definition. Such standards have recently been put in place for cross-national assessments where students from several countries take the same test. But fewer such mappings are available yet for national assessments."
    }), "\n", _jsx(_components.p, {
      children: "One assessment, the Trends in International Mathematics and Science Study (TIMSS), takes place every four years and allows us to look at progress over time among grade 4 and grade 8 students. It shows where progress has been particularly slow. In the United States, for example, the percentage of grade 8 students who met the minimum proficiency for mathematics increased from 61% in 1995 to only 70% in 2015, before declining to 55% in 2023."
    }), "\n", _jsxs(_components.p, {
      children: ["Some countries, such as ", _jsx(HighlightByIndex, {
        index: "0",
        children: "Malaysia"
      }), ", have even seen the share of students achieving minimum learning levels decrease over time."]
    }), "\n", _jsxs(_components.p, {
      children: ["In other countries, such as ", _jsx(HighlightByIndex, {
        index: "1",
        children: "Bahrain"
      }), ", progress had been fast – the share of students with minimum proficiency more than tripled between 2003 and 2019, before declining in 2023, in line with trends observed in many countries."]
    }), "\n", _jsx(Figure, {
      id: "PIRLSGAIN"
    }), "\n", _jsxs(IndicatorReference, {
      id: "4.1.1",
      label: "4.1.1b",
      children: [_jsx(_components.p, {
        children: "Contrary to most people's expectations, the gender learning gaps are not wide – spanning no more than ten percentage points for nearly all countries in primary education, for instance."
      }), _jsx(_components.p, {
        children: "But learning gaps between countries are huge. In sub-Saharan African countries, which tend to fall towards the lower end of reading skills in primary education, the share of students attaining minimum proficiency spans from just 5% in Burundi to 76% in Gabon (both results from PASEC 2019)."
      }), _jsx(_components.p, {
        children: "In mathematics in primary education for Southeast Asian countries, rates range from just 17% in the Lao People's Democratic Republic to 89% in Viet Nam (both results from SEA-PLM 2024)."
      }), _jsx(Figure, {
        id: "ASSESS"
      })]
    }), "\n", _jsx(_components.h2, {
      id: "adults",
      children: "Adults"
    }), "\n", _jsxs(IndicatorReference, {
      id: "4.6.2",
      children: [_jsx(_components.p, {
        children: "Between 2000 and 2024, the percentage of adults who are literate increased from 81% to 88% globally, while the percentage of youth who are literate increased from 87% to 93%."
      }), _jsx(_components.p, {
        children: "Despite these improvements, illiteracy remains widespread in many countries, especially among women. In Latin America and the Caribbean, stalled progress at relatively high levels reflects pockets of deeply disadvantaged “hard to reach” groups. In Ecuador, for instance, the literacy rate for adult women in rural areas has not changed much for the past 20 years, from 81% in 2001 to 87% in 2024."
      }), _jsx(_components.p, {
        children: "In Sub-Saharan Africa, over 40% of adult women (25-64 year-olds) cannot read. In many countries, progress has stalled at very low levels as in Mali, where less than 22% can read, or been slow as in Madagascar, which has improved from 66% to 69% over 20 years."
      }), _jsx(_components.p, {
        children: "Globally, if more adults are literate today than they were, it is mostly because more adults went to school when they were young rather than because adults who did not go to school had a second chance to learn. To verify this claim, the below graph shows data that follows the literacy skills of a given cohort – or group – as it grows older."
      }), _jsxs(ScrollyArea, {
        children: [_jsx(ScrollySticky, {
          children: _jsx(Figure, {
            id: "LITCOHORT"
          })
        }), _jsx(ScrollySection, {
          children: _jsxs(_components.p, {
            children: ["In most countries, literacy among a particular ", _jsx(HighlightByIndex, {
              index: "0",
              children: "cohort of adults"
            }), " stagnated or even declined over time as skills were underutilized."]
          })
        }), _jsx(ScrollySection, {
          children: _jsxs(_components.p, {
            children: ["In ", _jsx(_components.strong, {
              children: "Burkina Faso"
            }), ", the blue line shows that the literacy rate of ", _jsx(HighlightByIndex, {
              index: "0",
              children: "women aged 20–34 years in 2003"
            }), ", ", _jsx(HighlightByIndex, {
              index: "0",
              children: "27-41 years in 2010"
            }), " and ", _jsx(HighlightByIndex, {
              index: "0",
              children: "38–62 years in 2021"
            }), " remained relatively stable. And yet, because more young women are becoming literate, the red line shows that ", _jsx(HighlightByIndex, {
              index: "1",
              children: "the literacy rate among 20-34 year-old women"
            }), " has been increasing over time."]
          })
        })]
      })]
    }), "\n", _jsxs(IndicatorReference, {
      id: "4.6.1",
      children: [_jsx(_components.p, {
        children: "The literacy skills that these assessments are measuring are very basic. They show if people are able to read a simple sentence. Functional literacy, whereby people can read and interpret written instructions, formal letters, or contracts is a different matter."
      }), _jsx(_components.p, {
        children: "Few international surveys collect functional literacy data, much less on a regular basis."
      }), _jsx(_components.p, {
        children: "From the data we do have, we can see that 1 in 10 adults in France, Italy and New Zealand and 1 in 4 in Chile are not functionally literate (i.e. they fall below Level 1 of proficiency) in functional literacy."
      }), _jsx(Figure, {
        id: "LITPROF"
      })]
    }), "\n", _jsx(_components.h2, {
      id: "conclusion",
      children: "Conclusion"
    }), "\n", _jsx(_components.p, {
      children: "Although SDG 4 has shifted emphasis on monitoring learning, many countries still do not report towards the learning outcome indicators on reading and mathematics. Even countries that want to start assessing and reporting on learning often face obstacles preventing them from adopting effective and cost efficient solutions. But the evidence we do have suggests that most students in the world’s poorest countries do not achieve minimum proficiency in the most basic skills."
    }), "\n", _jsx(RelatedMaterial, {
      children: _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://openknowledge.worldbank.org/handle/10986/32553",
            children: "Ending learning poverty – what will it take? – World Bank"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "http://uis.unesco.org/sites/default/files/documents/ip61-combining-data-out-of-school-children-completion-learning-offer-more-comprehensive-view-sdg4.pdf",
            children: "Combining Data on Out-of-School Children, Completion and Learning to Offer a More Comprehensive View on SDG 4 – UIS"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://gemreportunesco.wordpress.com/2019/05/20/the-learning-assessment-market-pointers-for-countries-part-2/",
            children: "The learning assessment market - pointers for countries"
          })
        }), "\n"]
      })
    })]
  });
}
export default function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = {
    ..._provideComponents(),
    ...props.components
  };
  return MDXLayout ? _jsx(MDXLayout, {
    ...props,
    children: _jsx(_createMdxContent, {
      ...props
    })
  }) : _createMdxContent(props);
}
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
